// NFT position utility functions for Aerodrome Slipstream

/**
 * Convert tick to price using the formula: price = 1.0001^tick
 * @param tick The tick value (can be negative)
 * @returns The price as a number
 */
export function tickToPrice(tick: number): number {
  return Math.pow(1.0001, tick);
}

/**
 * Convert tick to price with proper decimal handling for USDC/WETH pairs
 * @param tick The tick value
 * @param token0Decimals Decimals of token0 (WETH = 18)
 * @param token1Decimals Decimals of token1 (USDC = 6)
 * @returns The price as a number
 */
export function tickToPriceWithDecimals(tick: number, token0Decimals: number = 18, token1Decimals: number = 6): number {
  const price = Math.pow(1.0001, tick);
  // Adjust for decimal difference: USDC has 6 decimals, WETH has 18 decimals
  // So we need to multiply by 10^(18-6) = 10^12
  return price * Math.pow(10, token0Decimals - token1Decimals);
}

/**
 * Convert price to tick using the formula: tick = log(price) / log(1.0001)
 * @param price The price value
 * @returns The tick as a number (rounded to nearest integer)
 */
export function priceToTick(price: number): number {
  return Math.round(Math.log(price) / Math.log(1.0001));
}

/**
 * Format tick range as human-readable price range
 * @param tickLower Lower tick
 * @param tickUpper Upper tick
 * @param token0Symbol Symbol of token0 (e.g., "WETH")
 * @param token1Symbol Symbol of token1 (e.g., "USDC")
 * @param token0Decimals Decimals of token0 (WETH = 18)
 * @param token1Decimals Decimals of token1 (USDC = 6)
 * @returns Formatted price range string
 */
export function formatTickRange(
  tickLower: number,
  tickUpper: number,
  token0Symbol: string,
  token1Symbol: string,
  token0Decimals: number = 18,
  token1Decimals: number = 6
): string {
  const priceLower = tickToPriceWithDecimals(tickLower, token0Decimals, token1Decimals);
  const priceUpper = tickToPriceWithDecimals(tickUpper, token0Decimals, token1Decimals);
  
  // For WETH/USDC, show USDC per WETH
  if (token0Symbol === "WETH" && token1Symbol === "USDC") {
    return `$${priceLower.toFixed(0)} - $${priceUpper.toFixed(0)}`;
  }
  
  // For other pairs, show token1 per token0
  return `${priceLower.toFixed(6)} - ${priceUpper.toFixed(6)} ${token1Symbol}/${token0Symbol}`;
}

/**
 * Check if current price is within the tick range
 * @param currentTick Current tick from pool
 * @param tickLower Lower tick of position
 * @param tickUpper Upper tick of position
 * @returns true if current price is within range
 */
export function isInRange(currentTick: number, tickLower: number, tickUpper: number): boolean {
  return currentTick >= tickLower && currentTick <= tickUpper;
}

/**
 * Calculate position value in USD using liquidity and current price
 * This is a simplified calculation - for production, you'd want more sophisticated math
 * @param liquidity Position liquidity
 * @param currentPrice Current price of the pair
 * @param token0Decimals Decimals of token0
 * @param token1Decimals Decimals of token1
 * @returns Estimated USD value (placeholder calculation)
 */
export function calculateNftPositionValue(
  liquidity: string,
  currentPrice: number,
  _token0Decimals: number = 18,
  _token1Decimals: number = 6
): number {
  // This is a simplified calculation
  // In reality, you'd need to calculate the actual token amounts based on liquidity and tick range
  const liquidityNum = parseFloat(liquidity);
  
  // Very rough estimate: assume liquidity represents a value proportional to current price
  // This is not accurate but gives a ballpark figure
  return liquidityNum * currentPrice * 0.000001; // Adjust multiplier as needed
}

/**
 * Get fee tier description from fee value
 * @param fee Fee value (e.g., 100 = 0.01% = 1 basis point)
 * @returns Human-readable fee description
 */
export function getFeeTierDescription(fee: number): string {
  const feePercent = fee / 10000; // Convert basis points to percentage
  
  // Standard fee tiers
  if (fee === 100) return "0.01% (CL100)";
  if (fee === 500) return "0.05% (CL500)";
  if (fee === 3000) return "0.3% (CL3000)";
  if (fee === 10000) return "1% (CL10000)";
  
  // For dynamic fees, show the actual percentage with more precision
  if (fee < 1000) {
    return `${feePercent.toFixed(4)}% (Dynamic)`;
  } else {
    return `${feePercent.toFixed(2)}% (Dynamic)`;
  }
}
